import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { ChannelWithMembership, NotifyLevel } from '@avslack/shared';
import { useToggleMembershipFlag } from '../data/channels';
import { Icon } from './Icon';

/**
 * Csatornankenti ertesites - a fejlec harangja mogott.
 *
 * Korabban a harang egyetlen nemito kapcsolo volt. A nemitas megmaradt (a
 * menu aljan), mert mast jelent, mint a "soha": a nemitott csatorna az
 * oldalsavban is elhalvanyul, es az emlites atjut rajta. A "soha" csak az
 * ertesitesrol szol, de arrol teljesen - az emlites sem szol.
 *
 * Az ELREJTES a nemitasnal is erosebb: a csatorna kikerul az oldalsavbol, es
 * csak a szemelyes @emlites szol. Ugyanez a Beallitasok > Csatornak fulon is
 * kapcsolhato - itt azert van meg, hogy egy elrejtett csatornat megnyitva
 * helyben vissza lehessen tenni a listaba.
 *
 * A szabalyok a szerveren vannak, lasd `decideNotification`.
 */
const OPTIONS: [NotifyLevel, string, string][] = [
  ['inherit', 'Alapértelmezés', 'A Beállításokban megadott szint szerint.'],
  ['always', 'Mindig', 'Minden üzenet szól, munkaidőn kívül is.'],
  ['all', 'Minden üzenet', 'Munkaidőben minden üzenet szól.'],
  ['mentions', 'Csak említés és DM', 'A sima üzenet nem szól.'],
  ['none', 'Soha', 'Semmi sem szól, még az említés sem.'],
];

export function ChannelNotifyMenu({ channel }: { channel: ChannelWithMembership }) {
  const [open, setOpen] = useState(false);
  const update = useToggleMembershipFlag();
  const { notifyLevel, isMuted, isHidden } = channel.membership;
  // DM-et nem rejtunk: az egy ember, nem egy csatorna (a Beallitasok listaja sem mutatja).
  const canHide = channel.kind === 'public' || channel.kind === 'private';
  // A rejtes es a nemitas felulirja a szintet - a valaszto ilyenkor nem szamit.
  const overridden = isMuted || isHidden;

  const set = (patch: { notifyLevel?: NotifyLevel; isMuted?: boolean; isHidden?: boolean }) =>
    update.mutate({ channelId: channel.id, patch });

  // A harang akkor "aktiv", ha a csatorna eltér az alapertelmezestol.
  const custom = overridden || notifyLevel !== 'inherit';
  const label = isHidden
    ? 'Értesítések: elrejtve, csak személyes említés'
    : isMuted
      ? 'Értesítések: némítva'
      : notifyLevel === 'always'
        ? 'Értesítések: mindig'
        : notifyLevel === 'none'
          ? 'Értesítések: soha'
          : 'Értesítések';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          title={label}
          aria-label={label}
          className={[
            'hidden size-8 place-items-center rounded-control transition-colors duration-(--duration-quick) sm:grid',
            custom
              ? 'bg-accent-soft text-accent'
              : 'text-ink-faint hover:bg-surface-hover hover:text-ink',
          ].join(' ')}
        >
          <Icon name={overridden || notifyLevel === 'none' ? 'ertesitesNema' : 'ertesites'} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="end"
          className="animate-fade-rise z-50 w-72 rounded-card border border-border-soft bg-surface-raised p-3 shadow-(--shadow-modal)"
        >
          <h3 className="mb-2 font-display text-sm font-semibold">
            Értesítések ebből a csatornából
          </h3>

          <div role="radiogroup" aria-label="Értesítési szint" className="space-y-1.5">
            {OPTIONS.map(([value, title, hint]) => (
              <label key={value} className="flex cursor-pointer items-start gap-2.5 text-sm">
                <input
                  type="radio"
                  name={`notify-${channel.id}`}
                  checked={notifyLevel === value}
                  disabled={overridden}
                  onChange={() => set({ notifyLevel: value })}
                  className="mt-1 accent-[var(--accent)]"
                />
                <span className={overridden ? 'text-ink-faint' : undefined}>
                  {title}
                  <span className="block text-xs text-ink-muted">{hint}</span>
                </span>
              </label>
            ))}
          </div>

          <label className="mt-3 flex cursor-pointer items-start gap-2.5 border-t border-border-soft pt-3 text-sm">
            <input
              type="checkbox"
              checked={isMuted}
              disabled={isHidden}
              onChange={(event) => set({ isMuted: event.target.checked })}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              Némítás
              <span className="block text-xs text-ink-muted">
                Halványan jelenik meg az oldalsávban, és csak az említés szól — a VIP-k sima üzenete
                sem.
              </span>
            </span>
          </label>

          {canHide && (
            <label className="mt-2 flex cursor-pointer items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={isHidden}
                onChange={(event) => set({ isHidden: event.target.checked })}
                className="mt-1 accent-[var(--accent)]"
              />
              <span>
                Elrejtés a csatornalistából
                <span className="block text-xs text-ink-muted">
                  Csak a Beállítások › Csatornák fülön látszik, és csak a személyes @említés szól.
                </span>
              </span>
            </label>
          )}

          {update.isError && (
            <p role="alert" className="mt-2 text-xs text-danger">
              Nem sikerült menteni. Próbáld újra.
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
